import React, { useEffect, useState, useMemo } from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

import leafimg from "../../assets/images/Leaf.png";
import axios from "../../appUtils/axiosConfig";
import { ProductCard } from "../products/card";

export default function HomeBestSellers({
  heading,
  categoryId,
  filters = {},
  limit = 8,
}) {
  const [productslist, setProductsList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const BaseUrl = process.env.REACT_APP_BASE_URL;

  const filtersKey = useMemo(() => JSON.stringify(filters || {}), [filters]);

  // Fully responsive slider settings
  const settings = {
    dots: true,
    infinite: true,
    speed: 600,
    slidesToShow: 4,
    slidesToScroll: 1,
    arrows: true,
    autoplay: true,
    autoplaySpeed: 2500,
    lazyLoad: "ondemand",
    responsive: [
      {
        breakpoint: 1920, // Large desktop
        settings: {
          slidesToShow: 4,
          slidesToScroll: 1,
          arrows: true,
        },
      },
      {
        breakpoint: 1536, // Desktop XL
        settings: {
          slidesToShow: 4,
          slidesToScroll: 1,
          arrows: true,
        },
      },
      {
        breakpoint: 1280, // Desktop LG
        settings: {
          slidesToShow: 3,
          slidesToScroll: 1,
          arrows: true,
        },
      },
      {
        breakpoint: 1024, // Tablet LG
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
          arrows: true,
        },
      },
      {
        breakpoint: 768, // Tablet MD
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
          arrows: false,
          dots: true,
        },
      },
      {
        breakpoint: 640, // Mobile LG
        settings: {
          slidesToShow: 1.5,
          slidesToScroll: 1,
          arrows: false,
          dots: true,
          centerMode: false,
        },
      },
      {
        breakpoint: 480, // Mobile SM
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          arrows: false,
          dots: true,
          centerMode: false,
        },
      },
      {
        breakpoint: 360, // Mobile XS
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          arrows: false,
          dots: true,
          centerMode: false,
        },
      },
    ],
  };

  useEffect(() => {
    let mounted = true;
    async function fetchProducts() {
      setIsLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams();
        if (categoryId) params.set("category_id", categoryId);
        Object.entries(filters || {}).forEach(([k, v]) => {
          if (v !== undefined && v !== null && v !== "")
            params.set(k, String(v));
        });
        params.set("page", "1");
        params.set("limit", String(limit));

        const res = await axios.get(
          `products/get_active_products.php?${params.toString()}&pincode=${localStorage.getItem("delivery_pincode") || ""}`
        );
        if (!mounted) return;
        setProductsList(res?.data?.data || []);
      } catch (err) {
        if (!mounted) return;
        setError(
          err?.response?.data || err.message || "Failed to load products"
        );
      } finally {
        if (mounted) setIsLoading(false);
      }
    }
    fetchProducts();
    return () => {
      mounted = false;
    };
  }, [categoryId, limit, filtersKey]);

  const hasProducts = Array.isArray(productslist) && productslist.length > 0;

  if (!hasProducts && !isLoading && !error) {
    return null;
  }

  return (
    <section className="py-6 sm:py-8 md:py-10 lg:py-12 container mx-auto font-archivo px-4 sm:px-6 lg:px-8">
      <div className="w-full max-w-7xl mx-auto">
        {/* Header with leaf icon */}
        {heading && (hasProducts || isLoading || error) && (
          <div className="flex justify-center items-center mb-6 sm:mb-8 md:mb-10">
            <div className="flex items-center gap-2 sm:gap-3">
              <img
                src={leafimg}
                alt="leaf"
                className="w-6 h-6 sm:w-7 sm:h-7 md:w-14 md:h-16"
              />
              <h2 className="text-xl sm:text-2xl md:text-3xl font-semibold text-gray-800 mb-0">
                {heading}
              </h2>
            </div>
          </div>
        )}

        {/* Product Slider or status messages */}
        {isLoading ? (
          <div className="p-6 sm:p-8 md:p-12 text-center">
            <div className="inline-block">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#77C712]"></div>
            </div>
            <p className="mt-4 text-gray-600">Loading products...</p>
          </div>
        ) : error ? (
          <div className="p-6 sm:p-8 md:p-12 text-center">
            <p className="text-red-500 font-medium text-sm sm:text-base">
              {String(error)}
            </p>
          </div>
        ) : (
          hasProducts && (
            <div className="w-full slider-container px-0 sm:px-2">
              <style jsx>{`
                .slider-container :global(.slick-slide) {
                  padding: 0 6px;
                }
                .slider-container :global(.slick-track) {
                  display: flex !important;
                }
                .slider-container :global(.slick-slide > div) {
                  display: flex !important;
                }
                .slider-container :global(.slick-dots) {
                  bottom: -40px;
                  margin-top: 20px;
                }
                .slider-container :global(.slick-dots li) {
                  width: auto;
                  margin: 0 4px;
                }
                .slider-container :global(.slick-dots li button) {
                  width: 8px;
                  height: 8px;
                  padding: 0;
                  border-radius: 50%;
                }
                .slider-container :global(.slick-prev:before),
                .slider-container :global(.slick-next:before) {
                  font-size: 24px;
                  color: #77c712;
                }
                .slider-container :global(.slick-prev),
                .slider-container :global(.slick-next) {
                  z-index: 1;
                  top: 50%;
                  transform: translateY(-50%);
                }
                .slider-container :global(.slick-prev) {
                  left: -30px;
                }
                .slider-container :global(.slick-next) {
                  right: -30px;
                }
                @media (max-width: 768px) {
                  .slider-container :global(.slick-prev) {
                    left: 0;
                    opacity: 0.5;
                  }
                  .slider-container :global(.slick-next) {
                    right: 0;
                    opacity: 0.5;
                  }
                  .slider-container :global(.slick-prev:before),
                  .slider-container :global(.slick-next:before) {
                    font-size: 18px;
                  }
                  .slider-container :global(.slick-dots) {
                    bottom: -30px;
                    margin-top: 12px;
                  }
                  .slider-container :global(.slick-dots li button) {
                    width: 6px;
                    height: 6px;
                  }
                }
                @media (max-width: 480px) {
                  .slider-container :global(.slick-prev),
                  .slider-container :global(.slick-next) {
                    display: none;
                  }
                  .slider-container :global(.slick-slide) {
                    padding: 0 4px;
                  }
                  .slider-container :global(.slick-dots) {
                    bottom: -25px;
                    margin-top: 8px;
                  }
                  .slider-container :global(.slick-dots li button) {
                    width: 5px;
                    height: 5px;
                  }
                }
              `}</style>
              <Slider {...settings}>
                {productslist.map((product) => (
                  <div key={product?.id} className="px-1 sm:px-2">
                    <ProductCard product={product} BaseUrl={BaseUrl} />
                  </div>
                ))}
              </Slider>
            </div>
          )
        )}
      </div>
    </section>
  );
}
